import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { PriorityType } from '@/Interface/ticketManagementType';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PermissionsService } from '@services/permissions.service';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, throwError } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ticket-priority',
  templateUrl: './ticket-priority.component.html',
  styleUrls: ['./ticket-priority.component.scss']
})
export class TicketPriorityComponent implements OnInit {
  priorityList: PriorityType[] = []
  public priorityForm: FormGroup;
  public pageSizeControl = new FormControl(10);
  pageSize: number;
  pageToGo: number = 1;
  searchElement = '';
  serialNumberArray: Array<number> = [];
  next_page: number;
  current_page: number;
  previous_page: number;
  total_pages: number;
  total_items: number;
  private latestQueryParams: any = {};
  sortColumn: string = null;
  sortOrder: 'asc' | 'desc' = null;
  searchSubject: Subject<string> = new Subject();
  loader: boolean = false;
  // Separate from `loader`: this one is scoped to the table, so a filter,
  // a search keystroke or a page change no longer blanks the whole page.
  tableLoader: boolean = false;
  isEditMode: boolean = false;

  public pageRecordOptions = [
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
    { value: '200', label: '200' },
  ];

  public selectedId: number;
  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady: boolean = false;

  constructor(
    private modalService: NgbModal,
    private _utilityService: UtilityService,
    private toaster: ToastrService,
    private formBuilder: FormBuilder,
    private permService: PermissionsService,
  ) {
    this.initializeForm()
  }

  ngOnInit(): void {
    this.initComponent()

  }
  async initComponent() {
    this.loader = true
    await this.getComponentPermissions();
    if (this.permissions.canView) {
      this.loader = false
      this.searchSubject.pipe(debounceTime(300)).subscribe((searchTerm: string) => {
        this.searchElement = searchTerm;
        this.updateQueryParams({ search: this.searchElement });
      });
      this.getTableData();
    }
  }
  async getComponentPermissions() {
    const url = this.permService.getCurrentUrl();
    // const menuFromLocal = this.permService.loadMenuFromLocalStorage();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();

    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
        canAdd: !!matchedMenu.canAdd,
        canEdit: !!matchedMenu.canEdit,
        canView: !!matchedMenu.canView,
        canDelete: !!matchedMenu.canDelete
      }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }

  get hasAnyActionPermission(): boolean {
    return (
      this.permissionsReady &&
      (this.permissions.canView ||
        this.permissions.canEdit ||
        this.permissions.canDelete)
    );
  }


  initializeForm(): void {
    this.priorityForm = this.formBuilder.group({
      id: [null],
      name: ['', [Validators.required, Validators.minLength(2)]],
      time: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/), Validators.min(0.01)]],
      // default: [false]
    });
  }

  minLengthArray(min: number) {
    return (control: FormControl) => {
      if (control.value && control.value.length >= min) {
        return null;
      }
      return { minLengthArray: { valid: false } };
    };
  }

  resetForm(): void {
    this.priorityForm.reset({
      id: null,
      name: '',
      time: '',
      // default: false
    });
    this.isEditMode = false;
  }


  openAddPopUp(content: any): void {
    this.isEditMode = false;
    this.resetForm();
    this.modalService.open(content, { centered: true, scrollable: true });
  }

  openEditPopUp(item: PriorityType, content: any): void {
    this.isEditMode = true;
    this.selectedId = item.id;
    this.priorityForm.patchValue({
      id: item.id,
      name: item.name,
      time: item.time,
      default: item.default
    });
    this.modalService.open(content, { centered: true, scrollable: true });
  }

  openViewPopUp(item: PriorityType, content: any): void {
    this.isEditMode = false;
    this.selectedId = item.id;
    this.priorityForm.patchValue({
      id: item.id,
      name: item.name,
      is_active: item.default
    });
    this.priorityForm.disable();
    this.modalService.open(content, { centered: true, scrollable: true }).result.then(
      () => {
        this.priorityForm.enable();
        this.resetForm();
      },
      () => {
        this.priorityForm.enable();
        this.resetForm();
      }
    );
  }

  onSubmit(): void {
    if (this.priorityForm.valid) {
      if (this.isEditMode) {
        this.updatePriority();
      } else {
        this.savePriority();
      }
    } else {
      Object.keys(this.priorityForm.controls).forEach(key => {
        this.priorityForm.get(key).markAsTouched();
      });
    }
  }

  savePriority(): void {
    const payload = {
      name: this.priorityForm.value.name.trim(),
      time: this.priorityForm.value.time
    };
    this._utilityService.addTicketPriority(payload).pipe(
      catchError(error => {
        this.handleApiFieldErrors(error, this.toaster)
        return throwError(() => error);
      })
    ).subscribe(response => {
      if (response) {
        this.getTableData();
        this.modalService.dismissAll();
        this.resetForm();
      }
    });
  }

  updatePriority(): void {
    const payload = {
      id: this.priorityForm.value.id,
      name: this.priorityForm.value.name.trim(),
      time: this.priorityForm.value.time
    };

    this._utilityService.editTicketPriority(payload).pipe(
      catchError(error => {
        this.handleApiFieldErrors(error, this.toaster)
        return throwError(() => error);
      })
    ).subscribe(response => {
      if (response) {
        this.toaster.success('Updated Successfully');
        this.getTableData();
        this.modalService.dismissAll();
        this.resetForm();
      }
    });

  }

  toggleDefaultPriority(item: PriorityType): void {
    const previousState = item.default;
    const newStatus = !previousState;

    const payload = {
      id: item.id,
      name: item.name,
      time: item.time,
      default: newStatus
    };

    this._utilityService.editTicketPriority(payload).pipe(
      catchError(error => {
        // Reset the toggle back to previous state
        this.getTableData();
        item.default = previousState;
        this.handleApiFieldErrors(error, this.toaster)
        return throwError(() => error);
      })
    ).subscribe(response => {
      if (response) {
        this.getTableData();
        this.toaster.success('Default priority updated successfully');
      }
    });
  }

  private updateQueryParams(paramsToUpdate: any): void {
    this.latestQueryParams = {
      ...this.latestQueryParams,
      ...paramsToUpdate
    };

    if (this.latestQueryParams.search === '') {
      this.latestQueryParams.search = null;
    }
    if (this.latestQueryParams.page === 1) {
      this.latestQueryParams.page = null;
    }
    if (this.latestQueryParams.size === 10) {
      this.latestQueryParams.size = null;
    }
    if (this.latestQueryParams.sort === null) {
      delete this.latestQueryParams.sort;
    }
    if (this.latestQueryParams.column === null) {
      delete this.latestQueryParams.column;
    }

    if (this.latestQueryParams.page === 1 || (this.latestQueryParams.page && (this.latestQueryParams.search))) {
      if (this.latestQueryParams.page > 1) {
        this.latestQueryParams.page = paramsToUpdate.page;
        if (this.latestQueryParams.size > 10) {
          this.latestQueryParams.size = paramsToUpdate.size;
        }
      } else {
        this.latestQueryParams.page = null;
      }
    }

    if (!this.next_page && (paramsToUpdate.size || paramsToUpdate.search)) {
      this.latestQueryParams = {
        size: paramsToUpdate.size || null,
        search: paramsToUpdate.search || null
      };
    }
    this.getTableData();
  }

  private getTableData(): void {
    const name = this.latestQueryParams['search'] || this.searchElement;
    const page = this.latestQueryParams['page'] ? +this.latestQueryParams['page'] : 1;
    const size = this.latestQueryParams['size'] ? +this.latestQueryParams['size'] : 10;
    const sort = this.latestQueryParams['sort'] || null;
    const column = this.latestQueryParams['column'] || null;
    this.getPriorityList(name, page, size, sort, column);
  }

  getPriorityList(name: string, page: number, size: number, sort: string | null, column: string | null): void {
    this.tableLoader = true;
    this.priorityList = [];
    this.serialNumberArray = [];
    this._utilityService
      .getTicketPriority(name, page, size, sort, column)
      .subscribe((res: any) => {
        if (res && res['pagination']) {
          this.priorityList = res.results.map((item: any, index: number) => ({
            ...item,
            srNo: (res.pagination.current_page - 1) * size + index + 1
          }));

          this.current_page = res.pagination.current_page;
          this.next_page = res.pagination.next_page;
          this.previous_page = res.pagination.previous_page;
          this.total_pages = res.pagination.total_pages;
          this.total_items = res.pagination.total_records;
          this.enablePagination();
        }
        this.tableLoader = false;
      }, (error) => {
        this.tableLoader = false;
        this.toaster.error('Failed to load data');
      });
  }

  public searchData(event: Event): void {
    this.searchElement = (event.target as HTMLInputElement)?.value || '';
    this.searchSubject.next(this.searchElement);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.total_pages) {
      this.updateQueryParams({ page: page });
    }
  }

  navigatePage(direction: string): void {
    if (direction === 'prev' && this.current_page > 1) {
      this.goToPage(this.current_page - 1);
    } else if (direction === 'next' && this.next_page !== null) {
      this.goToPage(this.current_page + 1);
    }
  }

  navigateToPage(): void {
    if (this.pageToGo >= 1 && this.pageToGo <= this.total_pages) {
      this.goToPage(this.pageToGo);
    }
  }

  public changePageSize(): void {
    this.pageSize = this.pageSizeControl.value!;
    this.updateQueryParams({ size: this.pageSize });
  }

  enablePagination(): void {
    const linkElement = document.getElementById('prevPage');
    if (linkElement) {
      if (this.previous_page === null) {
        linkElement.setAttribute('disabled', 'true');
        linkElement.style.pointerEvents = 'none';
        linkElement.style.color = 'gray';
      } else {
        linkElement.removeAttribute('disabled');
        linkElement.style.pointerEvents = 'auto';
        linkElement.style.color = '';
      }
    }
  }

  handleApiFieldErrors(error: HttpErrorResponse, toastr: ToastrService): void {

    // Case 1: status is an array
    if (error.error?.status && Array.isArray(error.error.status)) {
      error.error.status.forEach((msg: string) => toastr.error(msg));
      return;
    }

    // Case 2: single message
    if (error.error?.message) {
      toastr.error(error.error.message);
      return;
    }

    // Case 3: field validation errors
    if (error.error?.errors) {
      Object.keys(error.error.errors).forEach(key => {
        const message = Array.isArray(error.error.errors[key])
          ? error.error.errors[key][0]
          : error.error.errors[key];
        toastr.error(message);
      });
      return;
    }

    // Case 4: rules array with non_field_errors
    if (Array.isArray(error.error?.rules)) {
      error.error.rules.forEach((rule: any) => {
        if (Array.isArray(rule?.non_field_errors)) {
          rule.non_field_errors.forEach((msg: string) => {
            toastr.error(msg);
          });
        }
      });
      return;
    }
    // Case 5: root-level non_field_errors (Django / DRF style)
    if (Array.isArray(error.error?.non_field_errors)) {
      error.error.non_field_errors.forEach((msg: string) => {
        toastr.error(msg);
      });
      return;
    }

    // Fallback
    toastr.error('Sorry, something went wrong. Please try again.');
  }
  deleteRecord(id: number, content: any) {
    this.modalService.open(content, { centered: true }).result.then(
      (result) => {
        if (result === 'confirm') {
          this.deletePriority(id)
        }
      },
      (reason) => {
        console.log('Modal dismissed:', reason);
      }
    );
  }
  deletePriority(id: number) {
    if (id) {
      this._utilityService.deletePriority(id).pipe(
        catchError(error => {
          this.handleApiFieldErrors(error, this.toaster)
          this.resetForm();
          return throwError(() => error);
        })
      ).subscribe(() => {
        this.toaster.success('Deleted successfully')
        this.getTableData();
      });
    }
  }

}